/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react';

import { css } from '@emotion/css';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { PADDINGS, COLORS2_AS_BACKGROUND } from '^/constants/commonStyles';
import { ReservationDetailed } from '^/types/ReservationDetailed/RawReservationDetailed';
import { formatKRWWonAmount } from '^/util/formatKRWWonAmount';
import { parseFloatNumberOrNumberString } from '^/util/zod/numberFromString';

import { HoverToReveal } from '../../../../commonComponents/ui/HoverToReveal';
import { AdditionalFees2 } from '../../AdditionalFees2';
import { PODOnsitePaymentHoverMessage } from '../../onsiteInfoMessages';

import {
  CancelButtonStyle,
  billDivider,
  billLabel,
  billRow,
  billSubValue,
  billTitle,
  billValue,
} from './ReservationDetailDesktop.commonStuff';
import { CancellationWarningBeforePickup72Hours } from '../../CancellationWarningBeforePickup72hours';
import { ExtraChargeDisplayCurrency } from '../../extraChargeDisplay';

const wonCurrency = 'KRW';

function isBookedAndPODReservation(data: ReservationDetailed) {
  if (data.res_status !== 'booked' || data.pay_type !== 'POD') {
    throw new Error('DummyErrorForTypeFiltering');
  }
  return data;
}

export const ReservationDetailsBillPODBooked: React.FC<{
  data: ReturnType<typeof isBookedAndPODReservation>;
  onClickCancelReservation(reservationToCancel: ReservationDetailed): void;
  extraChargeDisplayCurrency?: ExtraChargeDisplayCurrency;
}> = observer(function ReservationDetailsBillPODBooked({
  data,
  onClickCancelReservation,
  extraChargeDisplayCurrency = 'foreign',
}) {
  const { t } = useTranslation();
  const currency = data.pay_type === 'POD' ? data.currency : data.extra_equipments_json.currency;
  const rate_foreigner_amount = parseFloatNumberOrNumberString(data.rate_foreigner_amount);
  const isKrwDisplay = extraChargeDisplayCurrency === 'krw';
  const isForeignCurrency = currency !== wonCurrency;
  const formatForeignAmount = (value?: number | string | null) => {
    if (value === null || value === undefined) return null;
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return String(value);
    return numericValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };
  return (
    <div className="w-full">
      <h3 className={billTitle}>{t('total')}</h3>

      <div
        id="separator1"
        className={[
          css`
            margin-top: 16px;
            margin-bottom: 16px;
          `,
          billDivider,
        ].join(' ')}
      />
      <div className="flex flex-col gap-[10px]">
        <div className={billRow}>
          <p className={billLabel}>대여 요금</p>
          <p className={billValue}>
            {isForeignCurrency
              ? formatForeignAmount(rate_foreigner_amount)
              : formatKRWWonAmount(rate_foreigner_amount)}{' '}
            {currency === wonCurrency ? t('won') : currency}
          </p>
        </div>
        {data.extra_charge && (data.extra_charge.additional_amount || 0) > 0 && (
          <div className={['mt-[5px]'].join(' ')}>
            <AdditionalFees2
              extra_charge={data.extra_charge}
              onSiteCurrency={data.extra_equipments_json.currency}
              displayCurrency={extraChargeDisplayCurrency}
            />
          </div>
        )}
        <div
          id="separator"
          className={[
            css`
              margin-top: 12px;
              margin-bottom: 12px;
            `,
            billDivider,
          ].join(' ')}
        />
        {!isKrwDisplay && (
          <div
            className={[
              billRow,
              css`
                position: relative;
              `,
            ].join(' ')}
          >
            <div
              className={[
                billLabel,
                css`
                  display: flex;
                  align-items: center;
                  justify-items: center;
                  height: fit-content;
                  gap: 6px;
                `,
              ].join(' ')}
            >
              <span>현장 결제 예상금액</span>
              <HoverToReveal
                toReveal={
                  <div
                    className={[
                      billSubValue,
                      PADDINGS.TOP_AND_BOTTOM.spacing16,
                      COLORS2_AS_BACKGROUND.sub.MrtGray3,
                      css`
                        position: absolute;
                        left: 0;
                        right: 0;
                        top: 70%;
                        padding-left: 10px;
                        padding-right: 10px;
                        border-radius: 10px;
                        text-align: left;
                      `,
                    ].join(' ')}
                  >
                    {PODOnsitePaymentHoverMessage}
                  </div>
                }
              />
            </div>
            <div>
              <div className="flex flex-col items-end gap-1.5">
                <p className={billValue}>
                  {isForeignCurrency
                    ? formatForeignAmount(data?.total_foreigner_amount)
                    : formatKRWWonAmount(data?.total_foreigner_amount)}
                  &nbsp;
                  {currency === wonCurrency ? t('won') : currency}
                </p>
                <p className={billSubValue}>
                  약 {formatKRWWonAmount(data?.total_amount)} {t('won')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      <div>
        <button
          type="button"
          onClick={() => {
            onClickCancelReservation(data);
          }}
          disabled={data.cancelDisabled}
          className={[CancelButtonStyle, 'mt-[10px]'].join(' ')}
        >
          {t('cancelBooking')}
        </button>
        <CancellationWarningBeforePickup72Hours show={data.show72HoursCancelWarning} />
      </div>
    </div>
  );
});
